/**
 * Capture des Pointer Events (et rien d'autre : pas de TouchEvent, pas de
 * MouseEvent).
 *
 * Regles gelees du CLAUDE.md : 1 doigt dessine, 2 doigts pan/zoom, seuil de
 * 8 px avant d'armer un clic de devinette.
 *
 * Les points sont accumules a la frequence du capteur (avec les evenements
 * coalesces quand le navigateur les expose) puis vides une seule fois par frame.
 * Le consommateur ne voit donc jamais plus d'un lot par rAF.
 */
import { CLICK_MOVE_THRESHOLD_PX } from '@retusch/shared';

/** Fenetre et tolerance du double appui : les valeurs usuelles des systemes. */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 32;
import type { Vec2 } from './geometry.js';

export type CaptureMode = 'draw' | 'inspect';

export interface GestureDelta {
  /** Translation en px CSS. */
  readonly dx: number;
  readonly dy: number;
  /** Facteur de zoom relatif a la frame precedente (1 = pas de zoom). */
  readonly scale: number;
  /** Centre du geste, en px CSS relatifs a l'element. */
  readonly center: Vec2;
}

export interface StrokeCaptureHandlers {
  /** Premier point d'un trait, en coordonnees zone normalisees. */
  onStrokeStart?(point: Vec2): void;
  /**
   * Lot de points RETENUS depuis la frame precedente, et `tip`, la derniere
   * position brute du pointeur.
   *
   * Les deux ne servent pas a la meme chose : les points retenus forment le
   * dessin ENCODE (un point coute 4 octets sur le fil), `tip` ne sert qu'a
   * l'affichage. Sans lui, l'encre s'arretait au dernier point retenu et
   * trainait donc derriere le curseur de tout l'espacement d'echantillonnage :
   * c'est la latence qu'on ressentait, et elle n'avait rien d'un probleme de
   * performance.
   */
  onStrokePoints?(points: readonly Vec2[], tip: Vec2): void;
  /** Fin du trait, avec la derniere position du pointeur. */
  onStrokeEnd?(tip: Vec2 | null): void;
  /** Un second doigt s'est pose : le trait en cours doit etre annule. */
  onStrokeCancel?(): void;
  onGesture?(delta: GestureDelta): void;
  /** Position du curseur souris/stylet, en px CSS. null quand il sort. */
  onHover?(point: Vec2 | null): void;
  /** Appui bref sans deplacement (mode inspect). */
  onTap?(view: Vec2): void;
  /**
   * Deux appuis brefs au meme endroit. Au doigt, c'est le geste attendu pour
   * zoomer : le pincement demande deux doigts et de la place, ce qu'un
   * telephone tenu d'une main n'a pas.
   */
  onDoubleTap?(view: Vec2): void;
}

export interface StrokeCaptureOptions {
  readonly element: HTMLElement;
  readonly mode: CaptureMode;
  /** clientX/clientY -> px CSS relatifs a l'element. */
  readonly toView: (clientX: number, clientY: number) => Vec2;
  /** clientX/clientY -> coordonnees zone normalisees 0-1. */
  readonly toZone: (clientX: number, clientY: number) => Vec2;
  /**
   * Distance minimale entre deux points retenus, en coordonnees zone.
   *
   * Une FONCTION, et non une constante : elle depend du zoom courant, pour que
   * le trait reponde a la meme distance parcourue a l'ecran quel que soit
   * l'agrandissement.
   */
  readonly minPointDistance: () => number;
  /**
   * Le dessin est-il autorise ICI et maintenant (sequence d'intro, timer fini,
   * outil choisi, point HORS de la zone…) ?
   *
   * Le point vise lui est passe, et ce n'est pas un detail : sans lui,
   * l'autorisation ne pouvait dependre que de l'outil, et un appui hors de la
   * zone armait un trait qui ne peindrait jamais rien. Quand cette fonction
   * repond non, le pointeur unique deplace la vue, chemin qui existait deja
   * pour la pipette et pour l'intro.
   */
  readonly canDraw: (zonePoint: Vec2) => boolean;
}

interface PointerState {
  x: number;
  y: number;
  readonly type: string;
}

export class StrokeCapture {
  private readonly options: StrokeCaptureOptions;
  private readonly handlers: StrokeCaptureHandlers;
  private readonly pointers = new Map<number, PointerState>();

  private drawingPointerId: number | null = null;
  private pending: Vec2[] = [];
  private lastEmitted: Vec2 | null = null;
  /** Derniere position du pointeur, NON filtree : la pointe affichee. */
  private lastRaw: Vec2 | null = null;
  private flushScheduled = 0;

  private gestureActive = false;
  private gestureDistance = 0;
  private gestureCenter: Vec2 = { x: 0, y: 0 };

  private pressOrigin: Vec2 | null = null;
  private pressMoved = 0;
  /** Dernier appui bref : instant et position, pour reconnaitre un double. */
  private lastTapAt = 0;
  private lastTapAt2: Vec2 | null = null;

  private detachFns: Array<() => void> = [];

  constructor(options: StrokeCaptureOptions, handlers: StrokeCaptureHandlers) {
    this.options = options;
    this.handlers = handlers;
  }

  attach(): void {
    const el = this.options.element;
    const on = <K extends keyof HTMLElementEventMap>(
      type: K,
      listener: (event: HTMLElementEventMap[K]) => void,
      opts?: AddEventListenerOptions,
    ): void => {
      el.addEventListener(type, listener as EventListener, opts);
      this.detachFns.push(() => el.removeEventListener(type, listener as EventListener, opts));
    };

    on('pointerdown', this.onPointerDown);
    on('pointermove', this.onPointerMove);
    on('pointerup', this.onPointerUp);
    on('pointercancel', this.onPointerUp);
    on('pointerleave', this.onPointerLeave);
    on('wheel', this.onWheel, { passive: false });
    on('contextmenu', (event) => event.preventDefault());
  }

  detach(): void {
    for (const off of this.detachFns) off();
    this.detachFns = [];
    if (this.flushScheduled !== 0) cancelAnimationFrame(this.flushScheduled);
    this.flushScheduled = 0;
    this.pointers.clear();
    this.drawingPointerId = null;
    this.gestureActive = false;
  }

  /** Interrompt le trait en cours (fin de timer, soumission). */
  /**
   * CLOT le trait en cours comme si le doigt s'etait leve.
   *
   * Sert a la fin de la phase de dessin : le trait n'entre dans le dessin qu'au
   * relachement, et un long trait encore en cours quand le chrono tombait etait
   * purement et simplement PERDU. Son auteur voyait sa retouche a l'ecran, mais
   * les autres recevaient un dessin qui ne la contenait pas.
   *
   * A ne pas confondre avec `abortStroke`, qui jette le trait : c'est le bon
   * geste quand un second doigt arrive (on commence un pincement, pas un
   * trait), et le mauvais quand la manche se termine.
   */
  finishStroke(): void {
    if (this.drawingPointerId === null) return;
    this.flush();
    this.drawingPointerId = null;
    this.lastEmitted = null;
    this.handlers.onStrokeEnd?.(this.lastRaw);
    this.lastRaw = null;
  }

  abortStroke(): void {
    if (this.drawingPointerId === null) return;
    this.drawingPointerId = null;
    this.pending = [];
    this.lastEmitted = null;
    this.lastRaw = null;
    this.handlers.onStrokeCancel?.();
  }

  // -------------------------------------------------------------------------

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    // Une selection encore active ailleurs dans la page (un pseudo, un message
    // du chat) se transforme en glisser-deposer de texte des qu'on repart du
    // canvas : le navigateur detourne alors les evenements de pointeur et le
    // trait reste fige a mi-course. On la replie avant de commencer.
    const selection = document.getSelection();
    if (selection !== null && !selection.isCollapsed) selection.removeAllRanges();

    this.options.element.setPointerCapture(event.pointerId);
    const view = this.options.toView(event.clientX, event.clientY);
    this.pointers.set(event.pointerId, { x: view.x, y: view.y, type: event.pointerType });

    this.pressOrigin = view;
    this.pressMoved = 0;

    if (this.pointers.size >= 2) {
      // Deuxieme doigt : on bascule en pan/zoom et le trait en cours est annule.
      this.abortStroke();
      this.beginGesture();
      return;
    }

    if (this.options.mode === 'draw') {
      const zonePoint = this.options.toZone(event.clientX, event.clientY);
      if (this.options.canDraw(zonePoint)) {
        this.drawingPointerId = event.pointerId;
        this.lastEmitted = zonePoint;
        this.lastRaw = zonePoint;
        this.handlers.onStrokeStart?.(zonePoint);
      }
    }
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const state = this.pointers.get(event.pointerId);

    if (state === undefined) {
      if (event.pointerType !== 'touch') {
        this.handlers.onHover?.(this.options.toView(event.clientX, event.clientY));
      }
      return;
    }

    const view = this.options.toView(event.clientX, event.clientY);
    this.pressMoved += Math.hypot(view.x - state.x, view.y - state.y);
    state.x = view.x;
    state.y = view.y;

    // Au doigt, la position ne remonte QUE pendant un trace : c'est ce dont la
    // loupe a besoin pour montrer ce que la main cache. Hors trace, un doigt
    // pose ne survole rien, il n'y a donc rien a signaler.
    if (event.pointerType !== 'touch' || this.drawingPointerId === event.pointerId) {
      this.handlers.onHover?.(view);
    }

    if (this.gestureActive) {
      this.updateGesture();
      return;
    }

    if (this.drawingPointerId === event.pointerId) {
      this.queuePoints(event);
      return;
    }

    // Un doigt qui ne dessine pas deplace la vue : vrai en mode inspect, mais
    // aussi pendant l'intro et quand la pipette est selectionnee.
    if (this.drawingPointerId === null && this.pointers.size === 1) {
      const previous = this.pressOrigin;
      this.handlers.onGesture?.({
        dx: view.x - (previous?.x ?? view.x),
        dy: view.y - (previous?.y ?? view.y),
        scale: 1,
        center: view,
      });
      this.pressOrigin = view;
    }
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.delete(event.pointerId);

    if (this.options.element.hasPointerCapture(event.pointerId)) {
      this.options.element.releasePointerCapture(event.pointerId);
    }

    const wasDrawing = this.drawingPointerId === event.pointerId;
    if (wasDrawing) {
      this.flush();
      this.drawingPointerId = null;
      this.lastEmitted = null;
      this.handlers.onStrokeEnd?.(this.lastRaw);
      this.lastRaw = null;
    }

    if (this.pointers.size < 2 && this.gestureActive) {
      this.gestureActive = false;
      // Le doigt restant repart d'ici pour ne pas faire sauter la vue.
      const remaining = [...this.pointers.values()][0];
      this.pressOrigin = remaining === undefined ? null : { x: remaining.x, y: remaining.y };
    }

    // Appui bref qui n'a pas produit de trait : clic de devinette en mode
    // inspect, prelevement de pipette en mode dessin.
    const brief =
      this.pointers.size === 0 &&
      this.pressMoved <= CLICK_MOVE_THRESHOLD_PX &&
      event.type === 'pointerup';

    if (brief && event.pointerType === 'touch') {
      const view = this.options.toView(event.clientX, event.clientY);
      const now = performance.now();
      const previous = this.lastTapAt2;
      const near =
        previous !== null && Math.hypot(view.x - previous.x, view.y - previous.y) <= DOUBLE_TAP_PX;
      if (near && now - this.lastTapAt <= DOUBLE_TAP_MS) {
        this.lastTapAt = 0;
        this.lastTapAt2 = null;
        this.handlers.onDoubleTap?.(view);
        return;
      }
      this.lastTapAt = now;
      this.lastTapAt2 = view;
    }

    if (!wasDrawing && brief) {
      this.handlers.onTap?.(this.options.toView(event.clientX, event.clientY));
    }
  };

  private readonly onPointerLeave = (event: PointerEvent): void => {
    if (event.pointerType !== 'touch') this.handlers.onHover?.(null);
  };

  private readonly onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    const view = this.options.toView(event.clientX, event.clientY);
    this.handlers.onGesture?.({
      dx: 0,
      dy: 0,
      scale: Math.exp(-event.deltaY * 0.0015),
      center: view,
    });
  };

  // -------------------------------------------------------------------------

  private beginGesture(): void {
    const [a, b] = [...this.pointers.values()];
    if (a === undefined || b === undefined) return;
    this.gestureActive = true;
    this.gestureDistance = Math.hypot(a.x - b.x, a.y - b.y);
    this.gestureCenter = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  private updateGesture(): void {
    const [a, b] = [...this.pointers.values()];
    if (a === undefined || b === undefined) return;

    const distance = Math.hypot(a.x - b.x, a.y - b.y);
    const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const scale = this.gestureDistance > 0 ? distance / this.gestureDistance : 1;

    this.handlers.onGesture?.({
      dx: center.x - this.gestureCenter.x,
      dy: center.y - this.gestureCenter.y,
      scale,
      center,
    });

    this.gestureDistance = distance;
    this.gestureCenter = center;
  }

  /**
   * Les evenements coalesces rendent les points intermediaires que le navigateur
   * a regroupes : c'est ce qui evite les segments droits sur un geste rapide.
   */
  private queuePoints(event: PointerEvent): void {
    const events =
      typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [event];
    const source = events.length > 0 ? events : [event];

    for (const raw of source) {
      const point = this.options.toZone(raw.clientX, raw.clientY);
      this.lastRaw = point;
      const last = this.lastEmitted;
      if (
        last !== null &&
        Math.hypot(point.x - last.x, point.y - last.y) < this.options.minPointDistance()
      ) {
        continue;
      }
      this.lastEmitted = point;
      this.pending.push(point);
    }

    // Une frame est demandee meme sans point retenu : la pointe, elle, a bouge,
    // et c'est tout ce qu'il faut pour que l'encre colle au curseur.
    if (this.flushScheduled === 0) {
      this.flushScheduled = requestAnimationFrame(() => {
        this.flushScheduled = 0;
        this.flush();
      });
    }
  }

  private flush(): void {
    const tip = this.lastRaw;
    // Rien a dire tant qu'aucune position n'a ete lue : le trait n'a pas encore
    // de pointe a suivre.
    if (tip === null) return;
    const batch = this.pending;
    this.pending = [];
    this.handlers.onStrokePoints?.(batch, tip);
  }
}
